// DOT Design System Token Values
// Extracted from Figma design specifications

import { DesignTokens } from '@/types/design-system'

export const designTokens: DesignTokens = {
  colors: {
    primary: {
      DEFAULT: '#2F4062',
      dark: '#141E2F',
      light: '#5A6B89',
    },
    secondary: {
      DEFAULT: '#6CACE4',
      dark: '#3B7CB6',
      light: '#A7CCF1',
    },
    success: {
      DEFAULT: '#3BA55D',
      light: '#E2FBE8',
      dark: '#2E8B4E',
    },
    error: {
      DEFAULT: '#E53935',
      light: '#FFD9D0',
      dark: '#D32F2F',
    },
    warning: {
      DEFAULT: '#F9A825',
      light: '#FEF9C3',
      dark: '#F57F17',
    },
    info: {
      DEFAULT: '#256EF4',
      light: '#E2F0FB',
      dark: '#1976D2',
    },
    white: '#FFFFFF',
    black: '#000000',
    purple: '#9153cf',
    purpleLight: '#e1cff6',
    gray: {
      0: '#F6F6F7',
      1: '#EBEBEB',
      2: '#D0D0D0',
      3: '#BDBDBD',
      4: '#707071',
      5: '#4A4B4C',
      6: '#1B1B1B',
    },
  },
  typography: {
    fontFamily: {
      sans: ['Pretendard GOV', 'system-ui', 'sans-serif'],
      logo: ['Jua', 'cursive'],
    },
    fontSize: {
      'title-xlarge': ['40px', { lineHeight: '1.193359375', fontWeight: '700' }],
      'title-large': ['32px', { lineHeight: '1.193359375', fontWeight: '700' }],
      'title-medium': ['24px', { lineHeight: '1.193359375', fontWeight: '700' }],
      'title-small': ['20px', { lineHeight: '1.193359375', fontWeight: '700' }],
      'title-xsmall': ['18px', { lineHeight: '1.193359375', fontWeight: '700' }],
      'body-xlarge': ['20px', { lineHeight: '1.193359375', fontWeight: '400' }],
      'body-large': ['18px', { lineHeight: '1.193359375', fontWeight: '400' }],
      'body-medium': ['16px', { lineHeight: '1.193359375', fontWeight: '400' }],
      'body-small': ['14px', { lineHeight: '1.193359375', fontWeight: '400' }],
      'body-xsmall': ['12px', { lineHeight: '1.193359375', fontWeight: '400' }],
      'body-xlarge-bold': ['20px', { lineHeight: '1.193359375', fontWeight: '600' }],
      'body-large-bold': ['18px', { lineHeight: '1.193359375', fontWeight: '600' }],
      'body-medium-bold': ['16px', { lineHeight: '1.193359375', fontWeight: '600' }],
      'body-small-bold': ['14px', { lineHeight: '1.193359375', fontWeight: '600' }],
      'body-xsmall-bold': ['12px', { lineHeight: '1.193359375', fontWeight: '600' }],
      'logo-text': ['25px', { lineHeight: '1', fontWeight: '400' }],
    },
  },
  spacing: {
    '18': '4.5rem',
    '88': '22rem',
  },
  borderRadius: {
    'icon': '0.5px',
  },
  boxShadow: {
    soft: '0 2px 15px 0 rgba(0, 0, 0, 0.08)',
    medium: '0 4px 25px 0 rgba(0, 0, 0, 0.1)',
  },
}

// Helper functions for accessing design tokens
export const getColor = (key: string): string => {
  const keys = key.split('.')
  let value: any = designTokens.colors

  for (const k of keys) {
    value = value[k]
    if (!value) break
  }

  return value || key
}

export const getFontSize = (key: keyof DesignTokens['typography']['fontSize']) => {
  return designTokens.typography.fontSize[key]
}

export const getSpacing = (key: string): string => {
  return designTokens.spacing[key] || key
}

// CSS custom properties generator
export const generateCSSVariables = (): string => {
  const cssVars: string[] = [':root {']
  
  // Colors
  Object.entries(designTokens.colors).forEach(([key, value]) => {
    if (typeof value === 'object') {
      Object.entries(value).forEach(([subKey, subValue]) => {
        const varName = subKey === 'DEFAULT' ? key : `${key}-${subKey}`
        cssVars.push(`  --color-${varName}: ${subValue};`)
      })
    } else {
      cssVars.push(`  --color-${key}: ${value};`)
    }
  })
  
  // Typography
  Object.entries(designTokens.typography.fontSize).forEach(([key, [size, props]]) => {
    cssVars.push(`  --font-size-${key}: ${size};`)
    cssVars.push(`  --line-height-${key}: ${props.lineHeight};`)
    cssVars.push(`  --font-weight-${key}: ${props.fontWeight};`)
  })
  
  // Spacing
  Object.entries(designTokens.spacing).forEach(([key, value]) => {
    cssVars.push(`  --spacing-${key}: ${value};`)
  })
  
  // Border radius
  Object.entries(designTokens.borderRadius).forEach(([key, value]) => {
    cssVars.push(`  --border-radius-${key}: ${value};`)
  })
  
  // Box shadow
  Object.entries(designTokens.boxShadow).forEach(([key, value]) => {
    cssVars.push(`  --box-shadow-${key}: ${value};`)
  })
  
  cssVars.push('}')
  
  return cssVars.join('\n')
}